import { format } from 'date-fns'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { ArrowBack } from '@mui/icons-material'
import { Box, Button, Grid, Stack, Typography } from '@mui/material'
import { QueryStatus } from '@reduxjs/toolkit/query'

import { useGetUserProfileQuery } from '../../../api/combined'
import { coreApi, usePostPublicFilesMutation } from '../../../api/core'
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { useAppSelector } from '../../../hooks/storeHooks'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import type { RootState } from '../../../store'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { useGetEditorialCardQuery, useSaveEditorialCardMutation } from '../../api/editorial-cards'
import { DiscardChangesDialog } from '../../components/DiscardChangesDialog/DiscardChangesDialog'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import { ContentImageUploader, EditorialCardForm } from '../../features/editorial-cards/components/EditorialCardForm/EditorialCardForm'
import { EditorialCardPreview } from '../../features/editorial-cards/components/EditorialCardPreview/EditorialCardPreview'
import {
  editorialCardFieldLabelKeys,
  EditorialCardFormValues,
  getUnstoredEditorialCardFields,
  needsPublishConfirmation,
  stickyEmailOptions,
  toEditorialCardFormValues,
  toEditorialCardMutation,
  toHtmlEditorImageUrl,
} from '../../features/editorial-cards/helpers/editorialCardHelpers'
import { StickyEmail } from '../../features/editorial-cards/types/EditorialCard'
import { MAX_PUBLIC_UPLOAD_FILE_SIZE_BYTES, MAX_PUBLIC_UPLOAD_FILE_SIZE_MB } from '../../helpers/fileUploadLimits'
import { useEditPageLeave } from '../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { editorialCardEditPath } from './EditorialCardsPage'

const listPath = '/internal/editorial-cards'

// The header image and content images are uploaded by their own components; saving meanwhile would drop the new image.
const isUploadingPublicFiles = (state: RootState) =>
  Object.values(state[coreApi.reducerPath].mutations).some(
    (mutation) => mutation?.endpointName === 'postPublicFiles' && mutation.status === QueryStatus.pending
  )

export const EditorialCardEditPage: FC = () => {
  const { cardId } = useParams<{ cardId?: string }>()
  usePage(pageService.getInternalPageWithId('internal-editorial-card-edit'))
  const { t } = useTranslation()
  const { run, notifySuccess, notifyError } = useMutationFeedback()
  const navigate = useNavigate()
  const cardQuery = useGetEditorialCardQuery(cardId ?? '', { skip: !cardId })
  const { data: currentUser } = useGetUserProfileQuery()
  const [saveCard, { isLoading: isSaving }] = useSaveEditorialCardMutation()
  const [uploadFiles] = usePostPublicFilesMutation()
  const isUploading = useAppSelector(isUploadingPublicFiles)
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<EditorialCardFormValues>>()
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<EditorialCardFormValues>({
    mode: 'onChange',
    defaultValues: toEditorialCardFormValues(undefined),
  })
  const values = useWatch({ control }) as EditorialCardFormValues
  const { guard, leave } = useEditPageLeave(isDirty)
  const card = cardId ? cardQuery.data : undefined
  const loadedCardId = useRef<string>()

  // Only on the first load of a card, so a refetch after saving can't replace edits that weren't stored.
  useEffect(() => {
    if (card && loadedCardId.current !== card.id) {
      loadedCardId.current = card.id
      reset(toEditorialCardFormValues(card))
    }
  }, [card, reset])

  const uploadContentImage = useCallback<ContentImageUploader>(
    async (file, fileName) => {
      if (file.size >= MAX_PUBLIC_UPLOAD_FILE_SIZE_BYTES) throw new Error(t('internal-common:image_too_large', { size: MAX_PUBLIC_UPLOAD_FILE_SIZE_MB }))
      try {
        const [uploaded] = await uploadFiles({ files: [new File([file], fileName, { type: file.type })], type: 'htmleditor', route: 'public' }).unwrap()
        return toHtmlEditorImageUrl(uploaded.url)
      } catch {
        throw new Error(t('internal-common:image_upload_failed'))
      }
    },
    [t, uploadFiles]
  )

  const save = useCallback(
    async (values: EditorialCardFormValues) => {
      if (!currentUser?.email) return
      const body = toEditorialCardMutation(card, values, currentUser.email)
      const saved = await run(saveCard(body).unwrap(), {
        error: t('internal-marketing:editorial_cards_save_failed'),
        errorWithReason: (reason) => t('internal-marketing:editorial_cards_save_failed_reason', { reason }),
      })
      if (!saved.ok) return
      const stored = saved.result
      const unstored = getUnstoredEditorialCardFields(body, stored)
      if (unstored.length) {
        const fields = unstored.map((field) => t(editorialCardFieldLabelKeys[field])).join(', ')
        notifyError(t('internal-marketing:editorial_cards_not_stored', { fields }))
        if (!card && stored.id) leave(editorialCardEditPath(stored.id), { replace: true })
        return
      }
      notifySuccess(t('internal-marketing:editorial_cards_saved', { name: values.titles.en }))
      leave(listPath)
    },
    [card, currentUser?.email, leave, notifyError, notifySuccess, run, saveCard, t]
  )

  const handleSave = handleSubmit((values) => {
    if (!needsPublishConfirmation(card, values)) {
      save(values)
      return
    }
    const isFuture = values.publishedAt > Date.now()
    const stickyEmailOption = stickyEmailOptions.find((option) => option.value === values.stickyEmail)
    setConfirmDialogData({
      title: t('internal-marketing:editorial_cards_publish_title'),
      confirmText: (
        <Stack spacing={1}>
          <Typography variant="body2">
            {isFuture
              ? t('internal-marketing:editorial_cards_publish_scheduled', { date: format(values.publishedAt, 'dd.MM.yyyy HH:mm') })
              : t('internal-marketing:editorial_cards_publish_now')}
          </Typography>
          {values.stickyEmail !== StickyEmail.No && stickyEmailOption && (
            <Typography variant="body2">{t('internal-marketing:editorial_cards_publish_email', { position: t(stickyEmailOption.labelKey) })}</Typography>
          )}
        </Stack>
      ),
      actionText: t('internal-marketing:editorial_cards_publish'),
      data: values,
    })
  })

  const handleConfirm = (confirmed?: ConfirmDialogData<EditorialCardFormValues>) => {
    setConfirmDialogData(undefined)
    if (confirmed?.data) save(confirmed.data)
  }

  const isLoading = !!cardId && cardQuery.isLoading
  const isLoadError = !!cardId && cardQuery.isError

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button startIcon={<ArrowBack />} onClick={() => navigate(listPath)}>
            {t('internal-common:back')}
          </Button>
          <Typography variant="h5" fontWeight="bold">
            {cardId ? t('internal-marketing:editorial_cards_edit') : t('internal-marketing:editorial_cards_new')}
          </Typography>
        </Stack>
        <Button variant="contained" onClick={handleSave} disabled={isLoading || isLoadError || isSaving || isUploading || !currentUser?.email}>
          {t('internal-common:save')}
        </Button>
      </Stack>

      <QueryStateBoundary isLoading={isLoading} isError={isLoadError} errorText={t('internal-marketing:editorial_cards_load_one_failed')} skeletonHeight={480}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <EditorialCardForm control={control} uploadContentImage={uploadContentImage} disabled={isSaving} />
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ position: 'sticky', top: (theme) => theme.spacing(2) }}>
              <EditorialCardPreview values={values} cardId={cardId} />
            </Box>
          </Grid>
        </Grid>
      </QueryStateBoundary>

      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
      <DiscardChangesDialog
        guard={guard}
        title={t('internal-marketing:editorial_cards_discard_title')}
        text={t('internal-marketing:editorial_cards_discard_confirm')}
        discardText={t('internal-marketing:editorial_cards_discard')}
      />
    </Box>
  )
}
